import { usePublicChatContext } from "@lazarentertainment/public-chat-web/dist/src/react"
import { ErrorBoundary } from "@sentry/nextjs"
import { useAuthContext } from "client/components/auth"
import { useChannelContext } from "client/components/channel"
import { useRef } from "react"
import { FormattedMessage } from "react-intl"
import ErrorMessage from "shared/components/ErrorMessage"
import styled from "styled-components"

import { ChatView } from "../constants"

import BannedUsersModal from "./BannedUsersModal"
import { useChatContext } from "./ChatContext"
import ChatHistory from "./chatHistory"
import { ChatInput } from "./chatInput"
import ChatTopBar from "./chatTopBar"
import ConfirmationModal from "./ConfirmationModal"
import DeleteMessageErrorModal from "./DeleteMessageErrorModal"
import JoinChatModal from "./JoinChatModal"
import QuotaLimitReachedErrorModal from "./QuotaLimitReachedErrorModal"

export const ChatContainer = styled.div<{
  $view: ChatView
}>`
  font-family: ${(props) => props.theme.font.primary};
  background-color: ${(props) =>
    props.$view === ChatView.OVERLAY
      ? "transparent"
      : props.theme.background};
  height: 100%;
  width: 100%;
  max-height: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
`

const InputRow = styled.div<{ $isMobileView: boolean; $view: ChatView }>`
  flex-shrink: 0;
  padding: ${(props) => props.theme.main.buttonMargin};

  display: flex;
  justify-content: flex-end;

  background-color: ${(props) =>
    props.$view === ChatView.OVERLAY
      ? "transparent"
      : props.theme.colors.white};
  border-top: 1px solid
    ${(props) =>
      props.$view === ChatView.OVERLAY
        ? "transparent"
        : props.theme.backgroundShade};

  ${(props) =>
    props.$isMobileView &&
    `
    padding: ${props.theme.mobile.buttonMargin};
  `}

  > :not(:first-child) {
    margin-left: ${(props) =>
      props.$isMobileView
        ? props.theme.mobile.buttonMargin
        : props.theme.main.buttonMargin};
  }
`

const ChatUi = () => {
  const {
    chatError,
    clearChatError,
    chatView,
    isMobileView,
    isJoinModalOpen,
    setIsJoinModalOpen,
    isBannedUsersModalOpen,
    setIsBannedUsersModalOpen,
    isDisableChatModalOpen,
    setIsDisableChatModalOpen,
    isUnbanModalOpen,
    setIsUnbanModalOpen,
    unbanModalUser,
    setUnbanModalUser,
    messages,
  } = useChatContext()
  const {
    banUser,
    unbanUser,
    toggleChatEnabledState,
    quotaRefreshSecondsRemaining,
    reactions,
    toggleReaction,
    deleteMessage,
  } = usePublicChatContext()
  const {
    auth: { user },
  } = useAuthContext()
  const { channelId, recordingId } = useChannelContext()

  const inputRef = useRef<HTMLInputElement>(null)

  return (
    <ErrorBoundary fallback={<ErrorMessage />}>
      <ChatContainer $view={chatView}>
        {isJoinModalOpen && (
          <JoinChatModal
            onClose={() => {
              setIsJoinModalOpen(false)
            }}
          />
        )}
        {isUnbanModalOpen && (
          <ConfirmationModal
            messageText={
              <FormattedMessage
                id="confirmationModal.unbanConfirmationMessage"
                defaultMessage="Are you sure you want to unban this user?"
              />
            }
            buttonText={
              <FormattedMessage
                id="confirmationModal.unbanButtonText"
                defaultMessage="Unban"
              />
            }
            close={() => {
              setIsUnbanModalOpen(false)
              setUnbanModalUser(null)
              setIsBannedUsersModalOpen(true)
            }}
            action={(callback: () => void) => {
              unbanUser(unbanModalUser).then(callback)
            }}
          />
        )}
        {isDisableChatModalOpen && (
          <ConfirmationModal
            messageText={
              <FormattedMessage
                id="confirmationModal.disableChatModalOpenMessage"
                defaultMessage="Are you sure you want to disable chat?"
              />
            }
            buttonText={
              <FormattedMessage
                id="disableChatModal.disable"
                defaultMessage="Disable"
              />
            }
            close={() => {
              setIsDisableChatModalOpen(false)
            }}
            action={(callback: () => void) => {
              toggleChatEnabledState(false)
              callback()
            }}
          />
        )}
        {isBannedUsersModalOpen && <BannedUsersModal />}
        {chatError?.code === "QuotaLimitReachedError" && (
          <QuotaLimitReachedErrorModal
            refreshTime={quotaRefreshSecondsRemaining}
            closeModal={clearChatError}
          />
        )}
        {chatError?.code === "DeleteMessageError" && (
          <DeleteMessageErrorModal closeModal={clearChatError} />
        )}
        <ChatTopBar />
        <ChatHistory
          uiView={chatView}
          messages={messages}
          messageReactions={reactions}
          submitMessageReaction={toggleReaction}
          banUser={banUser}
          deleteChatMessage={deleteMessage}
          showModerationTools={user?.channelId === channelId}
        />
        {!recordingId && (
          <InputRow $isMobileView={isMobileView} $view={chatView}>
            <ChatInput inputRef={inputRef} uiView={ChatView.DEFAULT} />
          </InputRow>
        )}
      </ChatContainer>
    </ErrorBoundary>
  )
}

export default ChatUi
